/// <amd-module name="Wiki/Renderer/Helpers" />

import { ITfsPageService } from "Tfs/Platform/Page";
import { isExternalUrl } from "VSS/Features/Markdown/Utils/UrlHelper";
import { UserContentAnchorHelper } from "VSS/Features/Markdown/Utils/UserContentAnchorHelper";
import { StateMergeOptions } from "VSS/Platform//History";
import { IVssPageContext } from "VSS/Platform/Context";
import { Uri } from "VSS/Platform/Util/Url";
import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { UrlConstants } from "Wiki/ViewCommon/Constants";
import { UrlParameters } from "Wiki/ViewCommon/Contracts";
import { decodeHyphenAndSpacesInPath } from "Wiki/ViewCommon/Utils/PathHelper";
import { getNewWikiState, getSimpleUrl, isFirefox, isSafari, routeUrlFromRouteTemplate } from "Wiki/ViewCommon/Utils/WikiHelper";

/**
 *  Checks if the given href is internal anchor link
 *   e.g. input: "#section"                                  output: true
 *        input: href = "..?pagePath=/page&anchor=section"   output: true
 *              currentPagePath = "/page"
 */
export function isInternalAnchorLink(href: string, pageContext?: IVssPageContext, wiki?: WikiV2, currentPagePath?: string, pageId?: number): boolean {
    const indexOfHash = href.indexOf("#");

    // It is internal anchor link (i.e., #section) if path starts with '#'
    let isInternalAnchorLink = indexOfHash === 0;

    // If href is absolute URL, and currentPagePath is provided then we look at URL to confirm whether it is internal anchor link
    if (!isInternalAnchorLink && currentPagePath) {
        try {
            const uri = new Uri(href);
            let isCurrentPage = uri.getQueryParam(UrlConstants.PagePathParam) === currentPagePath;

            if (!isCurrentPage && pageContext && wiki && wiki.name && pageId) {
                const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
                const data = tfsPageService.getData();

                if (data && data.project) {
                    const simpleUrl = getSimpleUrl(pageContext, {
                        [UrlConstants.ProjectParam]: data.project.name,
                        [UrlConstants.WikiIdentifierParam]: wiki.name,
                        [UrlConstants.PageIdParam]: pageId.toString()
                    });
                    isCurrentPage = uri.absoluteUri.indexOf(simpleUrl) > 0;
                }
            }
            const anchor = uri.getQueryParam(UrlConstants.AnchorParam);
            isInternalAnchorLink = isCurrentPage && !!anchor;
        } catch (e) {
            return false;
        }
    }

    return isInternalAnchorLink;
}

export function getExternalWikiHubPageUrl(pageContext: IVssPageContext, urlParams: UrlParameters): string {
    const stateParams: { [key: string]: string } = {};

    const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const data = tfsPageService.getData();
    if (data && data.project) {
        stateParams[UrlConstants.ProjectParam] = data.project.name;
    }

    const fullState = getNewWikiState(pageContext, { ...stateParams, ...urlParams }, {}, StateMergeOptions.none);
    const wikiOverviewRouteTemplate = "{project}/_wiki/wikis/{*wikiIdentifier}";
    const wikiHomePageUrl = routeUrlFromRouteTemplate(pageContext, wikiOverviewRouteTemplate, fullState);

    return wikiHomePageUrl;
}

export function wikiLinkOnClickEventHelper(event: React.MouseEvent<HTMLAnchorElement>, onClickHandler: () => void): boolean {
    const shouldOpenInNewTab = (event.ctrlKey && _isChrome()) || (event.metaKey && isSafari()) || (event.button === 1 && isFirefox()); // Middle click in firefox;
    const shouldExecuteOnClickHandler = !event.ctrlKey && !event.altKey && !event.shiftKey && !event.metaKey;
    if (!shouldOpenInNewTab && !shouldExecuteOnClickHandler) {
        // Returning true to execute default handler
        return true;
    }

    // Stop default execution
    event.stopPropagation();
    event.preventDefault();

    if (shouldOpenInNewTab) {
        // Open link in new tab for 'ctrl/command + click'. Uses href value to open url in new tab.
        const windowObject = window.open(event.currentTarget.href, "_blank");
        if (windowObject) {
            windowObject.opener = null;
        }
    } else if (shouldExecuteOnClickHandler) {
        // Execute provided onClickHandler for just 'click'
        onClickHandler();
    }

    // Returning false to prevent default handler from executing
    return false;
}

/**
 * Function to scroll to internal sections in a wiki page
 * @param anchor - section name where we want to scroll to
 * @returns true for unhandled anchors to allow default handler; false for anchor scroll handled here to stop default handler
 */
export function scrollToAnchor(anchor: string): boolean {
    if (anchor) {
        const element = getAnchorElement(anchor);
        if (element) {
            // scrollIntoView is supported by all supported browsers
            element.scrollIntoView();
            return false;
        }
    }
    return true;
}

/**
 * Function to focus selected internal section in a wiki page
 * @param anchor - section name we want to focus
 */
export function focusAnchor(anchor: string): void {
    if (anchor) {
        const element = getAnchorElement(anchor);
        if (element?.firstElementChild) {
            (element.firstElementChild as HTMLElement).focus();
        }
    }
}

function getAnchorElement(anchor: string): HTMLElement | null {
    if (!anchor) return null;
    // '+' in anchor name gets replaced with space while generating URL. Replacing space with '+' so that it gets encoded correctly.
    anchor = anchor.replace(" ", "+");
    // Find element on page with the adjusted ID or given ID
    const encodedAnchor = encodeURIComponent(anchor);
    const elementId = UserContentAnchorHelper.convertAnchorName(encodedAnchor);
    const element = document.getElementById(elementId) || document.getElementById(encodedAnchor);
    return element;
}

export function isBrokenLink(href: string, getWikiPage: (pagePath: string) => WikiPage | undefined, currentUrl: string): boolean {
    try {
        if (isUrlExternalToWiki(href, currentUrl)) {
            // To avoid checking cross project links.
            return false;
        }

        const uri = new Uri(href);
        let pagePath = uri.getQueryParam(UrlConstants.PagePathParam);
        pagePath = pagePath && pagePath.replace(/\\/g, "/");
        const decodedPagePath = pagePath && decodeHyphenAndSpacesInPath(pagePath);
        if (decodedPagePath) {
            return !getWikiPage(decodedPagePath.toLocaleLowerCase());
        }
    } catch (e) {
        return false;
    }

    return false;
}

export function isUrlExternalToWiki(url: string, currentUrl: string): boolean {
    if (isExternalUrl(url)) {
        return true;
    }

    return !_isSameWikiOfHost(url, currentUrl);
}

function _isSameWikiOfHost(targetUrl: string, currentPageurl: string): boolean {
    const targetUri = new Uri(targetUrl);
    const currentPageUri = new Uri(currentPageurl);
    // It is assumed that the target Url would be pagepath based url and not simplified url
    return currentPageUri.path.toLowerCase().startsWith(targetUri.path.toLowerCase());
}

// Export for testing
export function _isChrome(): boolean {
    const chrome = /chrome\/([\d+.]+)/i.exec(navigator.userAgent);
    return !!chrome;
}
